import dayjs from 'dayjs';
import { validateFixedChargeDayWindow } from '@charitystack/shared';

type Frequency = 'One-Time' | 'Daily' | 'Weekly' | 'Monthly' | 'Annually';
type RecurringFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Annually';

// Dashboard frequency label → backend frequency the shared resolver expects.
const DASHBOARD_TO_BACKEND_FREQ: Record<RecurringFrequency, string> = {
  Daily: 'DAILY',
  Weekly: 'WEEKLY',
  Monthly: 'MONTHLY',
  Annually: 'ANNUALLY',
};

const FREQUENCY_ORDER: Frequency[] = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
const RECURRING_FREQUENCIES: RecurringFrequency[] = ['Daily', 'Weekly', 'Monthly', 'Annually'];

type ProductOption = {
  productName?: string;
  productPrice?: string;
  [key: string]: any;
};

type ProductGroup = {
  productGroupName?: string;
  productGroupDescription?: string;
  products?: ProductOption[];
  [key: string]: any;
};

type ValidationResult = {
  errorMessages: string[];
  formData: any;
  invalidFields: Set<string>;
  warningMessages: string[];
};

const hasTextValue = (value: unknown): boolean =>
  typeof value === 'string' ? value.trim().length > 0 : value != null;

const hasProductData = (product?: ProductOption): boolean =>
  !!product && (hasTextValue(product.productName) || hasTextValue(product.productPrice));

const hasProductGroupData = (group?: ProductGroup): boolean =>
  !!group &&
  (hasTextValue(group.productGroupName) ||
    hasTextValue(group.productGroupDescription) ||
    (Array.isArray(group.products) && group.products.some(hasProductData)));

const cloneProductOption = (product: ProductOption): ProductOption => ({ ...product });

const cloneProductGroup = (group: ProductGroup): ProductGroup => ({
  ...group,
  products: Array.isArray(group.products) ? group.products.map(cloneProductOption) : [],
});

const getProductGroupMap = (formData: any): Record<Frequency, ProductGroup[]> => ({
  'One-Time': formData.oneTimeProductGroups || [],
  Daily: formData.dailyProductGroups || [],
  Weekly: formData.weeklyProductGroups || [],
  Monthly: formData.monthlyProductGroups || [],
  Annually: formData.annuallyProductGroups || [],
});

const productGroupMapToFormData = (
  formData: any,
  productGroupMap: Record<string, ProductGroup[]>,
) => ({
  ...formData,
  // The reconciled map only includes enabled frequencies. Preserve disabled
  // frequency groups so temporarily disabling a frequency does not erase work.
  oneTimeProductGroups: productGroupMap['One-Time'] ?? formData.oneTimeProductGroups ?? [],
  dailyProductGroups: productGroupMap.Daily ?? formData.dailyProductGroups ?? [],
  weeklyProductGroups: productGroupMap.Weekly ?? formData.weeklyProductGroups ?? [],
  monthlyProductGroups: productGroupMap.Monthly ?? formData.monthlyProductGroups ?? [],
  annuallyProductGroups: productGroupMap.Annually ?? formData.annuallyProductGroups ?? [],
});

const reconcileProductGroupsForEnabledFrequencies = (
  formData: any,
): Record<string, ProductGroup[]> => {
  const enabledFrequencies = (formData.checkedValues || []).filter((freq: string) => freq !== '');
  const rawOptionsMap = getProductGroupMap(formData);
  const reconciledMap: Record<string, ProductGroup[]> = {};

  for (const freq of enabledFrequencies) {
    const targetGroups = [...((rawOptionsMap[freq as Frequency] || []) as ProductGroup[])];
    const sourceFrequencies = FREQUENCY_ORDER.filter(
      (sourceFreq) => sourceFreq !== freq && enabledFrequencies.includes(sourceFreq),
    );
    const maxGroupCount = Math.max(
      targetGroups.length,
      ...sourceFrequencies.map(
        (sourceFreq) => ((rawOptionsMap[sourceFreq] || []) as ProductGroup[]).length,
      ),
    );

    for (let groupIndex = 0; groupIndex < maxGroupCount; groupIndex += 1) {
      const sourceGroup = sourceFrequencies
        .map((sourceFreq) => ((rawOptionsMap[sourceFreq] || []) as ProductGroup[])[groupIndex])
        .find((group) => hasProductGroupData(group));
      if (!sourceGroup) continue;

      if (!targetGroups[groupIndex]) {
        targetGroups[groupIndex] = cloneProductGroup(sourceGroup);
        continue;
      }

      let nextGroup = targetGroups[groupIndex];
      if (!hasTextValue(nextGroup.productGroupName) && hasTextValue(sourceGroup.productGroupName)) {
        nextGroup = { ...nextGroup, productGroupName: sourceGroup.productGroupName };
      }
      if (
        !hasTextValue(nextGroup.productGroupDescription) &&
        hasTextValue(sourceGroup.productGroupDescription)
      ) {
        nextGroup = {
          ...nextGroup,
          productGroupDescription: sourceGroup.productGroupDescription,
        };
      }

      const maxProductCount = Math.max(
        Array.isArray(nextGroup.products) ? nextGroup.products.length : 0,
        ...sourceFrequencies.map(
          (sourceFreq) =>
            ((rawOptionsMap[sourceFreq] || []) as ProductGroup[])[groupIndex]?.products?.length ||
            0,
        ),
      );
      const nextProducts = Array.isArray(nextGroup.products) ? [...nextGroup.products] : [];

      for (let productIndex = 0; productIndex < maxProductCount; productIndex += 1) {
        const sourceProduct = sourceFrequencies
          .map(
            (sourceFreq) =>
              ((rawOptionsMap[sourceFreq] || []) as ProductGroup[])[groupIndex]?.products?.[
                productIndex
              ],
          )
          .find((product) => hasProductData(product));
        if (!sourceProduct) continue;

        if (!nextProducts[productIndex]) {
          nextProducts[productIndex] = cloneProductOption(sourceProduct);
          continue;
        }

        let nextProduct = nextProducts[productIndex];
        if (!hasTextValue(nextProduct.productName) && hasTextValue(sourceProduct.productName)) {
          nextProduct = { ...nextProduct, productName: sourceProduct.productName };
        }
        if (!hasTextValue(nextProduct.productPrice) && hasTextValue(sourceProduct.productPrice)) {
          nextProduct = { ...nextProduct, productPrice: sourceProduct.productPrice };
        }
        nextProducts[productIndex] = nextProduct;
      }

      targetGroups[groupIndex] = { ...nextGroup, products: nextProducts };
    }

    reconciledMap[freq] = targetGroups;
  }

  return reconciledMap;
};

const isDatePassed = (dateString: string): boolean => {
  const date = dayjs(dateString);
  const today = dayjs().startOf('day');
  return date.isBefore(today);
};

type ScheduleValidationError =
  | null
  | 'start_date_missing'
  | 'end_date_missing'
  | 'start_date_in_past'
  | 'end_date_in_past'
  | 'start_after_end'
  | 'fixed_day_outside_window'
  | 'fixed_day_invalid_for_frequency';

const getScheduleValidationError = (config: any): ScheduleValidationError => {
  if (!config) return null;

  const startType = config.startDateType || 'none';
  const endType = config.endDateType || 'none';

  if (startType === 'dynamic' && config.startDateOffset == null) return 'start_date_missing';
  if (startType === 'fixed' && !config.fixedStartDate) return 'start_date_missing';
  if (endType === 'dynamic' && config.endDateOffset == null) return 'end_date_missing';
  if (endType === 'fixed' && !config.fixedEndDate) return 'end_date_missing';
  if (
    startType === 'dynamic' &&
    endType === 'dynamic' &&
    config.startDateOffset != null &&
    config.endDateOffset != null &&
    config.startDateOffset >= config.endDateOffset
  ) {
    return 'start_after_end';
  }

  if (
    startType === 'fixed' &&
    endType === 'fixed' &&
    config.fixedStartDate &&
    config.fixedEndDate &&
    !dayjs(config.fixedStartDate).isBefore(dayjs(config.fixedEndDate))
  ) {
    return 'start_after_end';
  }

  if (startType === 'dynamic' && endType === 'fixed' && config.startDateOffset != null) {
    const unit = (config.scheduleType || 'days').slice(0, -1) as dayjs.ManipulateType;
    if (!dayjs().add(config.startDateOffset, unit).isBefore(dayjs(config.fixedEndDate))) {
      return 'start_after_end';
    }
  }

  if (startType === 'fixed' && endType === 'dynamic' && config.endDateOffset != null) {
    const unit = (config.scheduleType || 'days').slice(0, -1) as dayjs.ManipulateType;
    if (!dayjs(config.fixedStartDate).isBefore(dayjs().add(config.endDateOffset, unit))) {
      return 'start_after_end';
    }
  }

  if (startType === 'fixed' && config.fixedStartDate && isDatePassed(config.fixedStartDate)) {
    return 'start_date_in_past';
  }
  if (endType === 'fixed' && config.fixedEndDate && isDatePassed(config.fixedEndDate)) {
    return 'end_date_in_past';
  }

  return null;
};

const getScheduleValidationMessage = (error: ScheduleValidationError): string | null => {
  switch (error) {
    case 'start_date_missing':
      return 'Please select a value for Default Start Date or set to None';
    case 'end_date_missing':
      return 'Please select a value for Default End Date or set to None';
    case 'start_date_in_past':
      return 'Default Start Date is in the past and will be ignored';
    case 'end_date_in_past':
      return 'Default End Date is in the past and will be ignored';
    case 'start_after_end':
      return 'Default Start Date must be before Default End Date';
    case 'fixed_day_outside_window':
      return 'Charge day falls outside the start/end window, so it would never be charged — widen the window or change the charge day.';
    case 'fixed_day_invalid_for_frequency':
      return 'Charge day isn’t valid for the selected frequency — pick a valid day.';
    default:
      return null;
  }
};

// Derive the schedule window bounds (as 'YYYY-MM-DD' or null) from a per-frequency
// config, mirroring how donation-elements' calculateScheduledDates resolves them:
//   start: fixed → fixedStartDate; dynamic → today + offset; none → null (today)
//   end:   fixed → fixedEndDate;   dynamic → today + offset; none → null (open-ended)
//
// IMPORTANT — past fixed dates are dropped, exactly as the runtime does
// (calculateScheduledDates only honors a fixed start/end when !isDatePassed).
// A LAPSED fixed end date therefore yields windowEnd=null (open-ended), NOT a
// closed window in the past. Without this, a fundraiser with a fixed charge day
// and an already-expired end date would be hard-blocked from saving on any edit
// — reversing the soft "past date is ignored" behavior. (Greptile PR #1922.)
const deriveScheduleWindow = (
  config: any,
): { windowStart: string | null; windowEnd: string | null } => {
  const startType = config.startDateType || 'none';
  const endType = config.endDateType || 'none';
  const unit = (config.scheduleType || 'days').slice(0, -1) as dayjs.ManipulateType;

  let windowStart: string | null = null;
  if (startType === 'fixed' && config.fixedStartDate && !isDatePassed(config.fixedStartDate)) {
    windowStart = config.fixedStartDate;
  } else if (startType === 'dynamic' && config.startDateOffset != null && config.startDateOffset > 0) {
    windowStart = dayjs().add(config.startDateOffset, unit).format('YYYY-MM-DD');
  }

  let windowEnd: string | null = null;
  if (endType === 'fixed' && config.fixedEndDate && !isDatePassed(config.fixedEndDate)) {
    windowEnd = config.fixedEndDate;
  } else if (endType === 'dynamic' && config.endDateOffset != null && config.endDateOffset > 0) {
    windowEnd = dayjs().add(config.endDateOffset, unit).format('YYYY-MM-DD');
  }

  return { windowStart, windowEnd };
};

const addAmountValidation = (
  invalidFields: Set<string>,
  errorMessages: string[],
  fieldName: string,
  label: string,
  value: string | undefined,
) => {
  if (!value || !value.trim()) {
    invalidFields.add(fieldName);
    errorMessages.push(`${label} is required`);
    return;
  }

  const amountValue = parseFloat(value);
  if (Number.isNaN(amountValue) || amountValue < 1) {
    invalidFields.add(fieldName);
    errorMessages.push(`${label} must be at least $1`);
  } else if (amountValue > 1000000) {
    invalidFields.add(fieldName);
    errorMessages.push(`${label} cannot exceed $1,000,000`);
  }
};

const addOptionalAmountValidation = (
  invalidFields: Set<string>,
  errorMessages: string[],
  fieldName: string,
  label: string,
  value: string | undefined,
) => {
  if (!value || !value.trim()) return;

  const amountValue = parseFloat(value);
  if (Number.isNaN(amountValue) || amountValue < 1) {
    invalidFields.add(fieldName);
    errorMessages.push(`${label} must be at least $1`);
  } else if (amountValue > 1000000) {
    invalidFields.add(fieldName);
    errorMessages.push(`${label} cannot exceed $1,000,000`);
  }
};

export function validateFundraiserFormForSave(
  formData: any,
  options: { descriptionPlainText?: string } = {},
): ValidationResult {
  const invalidFields = new Set<string>();
  const errorMessages: string[] = [];
  const warningMessages: string[] = [];
  let validatedFormData = formData;
  const checkedValues: string[] = Array.isArray(formData.checkedValues)
    ? formData.checkedValues
    : [];

  if (!formData.formName || !formData.formName.trim()) {
    invalidFields.add('form_configuration-appearance-fundraiserName');
    errorMessages.push('A fundraiser name is required');
  }
  if (!Array.isArray(formData.funds) || formData.funds.length === 0) {
    invalidFields.add('form_configuration-appearance-funds');
    errorMessages.push('At least one Fund is required');
  }
  if (!formData.color || formData.color.length !== 7 || !formData.color.startsWith('#')) {
    invalidFields.add('form_configuration-appearance-color');
    errorMessages.push('Color is required');
  }
  if (
    formData.showFundraisingBar &&
    (formData.targetAmount === '' || parseInt(formData.targetAmount, 10) <= 0)
  ) {
    invalidFields.add('form_configuration-appearance-targetAmount');
    errorMessages.push('A target amount greater than 0 is required');
  }
  if (
    formData.showFundraisingBar &&
    formData.trackingMetric === 'PRODUCT_COUNT' &&
    formData.formType !== 'PRODUCT'
  ) {
    invalidFields.add('form_configuration-appearance-trackingMetric');
    errorMessages.push('"Items" tracking requires a Sponsorship amount type');
  }

  const descriptionPlainText = options.descriptionPlainText || '';
  if (descriptionPlainText.length > 2500) {
    invalidFields.add('form_configuration-appearance-description');
    errorMessages.push('Description must be 2500 characters or less');
  }

  if (checkedValues.length === 0) {
    invalidFields.add('form_configuration-frequency-frequencies');
    errorMessages.push('At least one frequency must be enabled');
  }
  if (!formData.defaultDonationType || formData.defaultDonationType === '') {
    invalidFields.add('form_configuration-frequency-defaultFrequency');
    errorMessages.push('Default Frequency is required');
  }

  if (formData.requireScheduleDonations && formData.frequencyScheduledRanges) {
    for (const freq of RECURRING_FREQUENCIES) {
      const config = formData.frequencyScheduledRanges[freq];
      const error = getScheduleValidationError(config);
      const message = getScheduleValidationMessage(error);

      // Track whether the start/end window itself is already broken for this
      // frequency. If so, skip the fixed-charge-day window check below to avoid
      // reporting two overlapping errors for the same (already-invalid) window.
      //
      // Past-date cases are included on purpose: a lapsed fixed start/end is
      // IGNORED at runtime (treated as open-ended), so a fixed charge day must
      // NOT be hard-blocked just because the stored end date has expired. (This
      // is belt-and-suspenders alongside deriveScheduleWindow dropping past
      // fixed dates — either alone fixes the regression; together is safest.
      // Greptile PR #1922.)
      const windowAlreadyInvalid =
        error === 'start_date_missing' ||
        error === 'end_date_missing' ||
        error === 'start_after_end' ||
        error === 'start_date_in_past' ||
        error === 'end_date_in_past';

      if (message) {
        // A past default start/end date is informational only: the runtime ignores
        // it (the schedule simply starts/ends immediately), so we no longer surface
        // a "… is in the past and will be ignored" warning toast on save. Skip these
        // cases without pushing to warningMessages.
        if (error === 'start_date_in_past' || error === 'end_date_in_past') {
          // no-op (fall through to the fixed-day check below)
        } else {
          if (error === 'start_date_missing' || error === 'start_after_end') {
            invalidFields.add(`form_configuration-frequency-${freq}-startDate`);
          }
          if (error === 'end_date_missing' || error === 'start_after_end') {
            invalidFields.add(`form_configuration-frequency-${freq}-endDate`);
          }
          invalidFields.add(`form_configuration-frequency-${freq}-scheduleOffsetValidation`);
          errorMessages.push(`${freq}: ${message}`);
        }
      }

      // Fixed-charge-day validation (single source of truth in @charitystack/shared).
      // Blocks save when the configured fixed day can never fire inside the window
      // (the donation would never be charged) or is invalid for the frequency.
      if (config && config.fixedChargeDay != null && config.fixedChargeDay !== '' && !windowAlreadyInvalid) {
        const { windowStart, windowEnd } = deriveScheduleWindow(config);
        const fixedDayError = validateFixedChargeDayWindow({
          fixedChargeDay: config.fixedChargeDay,
          frequency: DASHBOARD_TO_BACKEND_FREQ[freq],
          windowStart,
          windowEnd,
          scheduledTime: config.scheduledTime || null,
        });
        const fixedDayMessage = getScheduleValidationMessage(
          fixedDayError as ScheduleValidationError,
        );
        if (fixedDayMessage) {
          // Per-frequency fields are still flagged inline (above) so the org sees
          // exactly which tab is wrong. The TOAST message is intentionally NOT
          // prefixed with the frequency: pushing the same unprefixed string for
          // every affected frequency lets getValidationToastMessage's Set dedup
          // collapse them into ONE short line instead of repeating
          // "Weekly: …; Monthly: …".
          invalidFields.add(`form_configuration-frequency-${freq}-fixedChargeDay`);
          invalidFields.add(`form_configuration-frequency-${freq}-scheduleOffsetValidation`);
          errorMessages.push(fixedDayMessage);
        }
      }
    }
  }

  const validFormTypes = ['EMBED_FORM', 'CROWDFUNDING_FORM', 'PRODUCT'];
  if (!formData.formType || !validFormTypes.includes(formData.formType)) {
    invalidFields.add('form_configuration-amounts-formType');
    errorMessages.push('Please select an Amount Button type');
  }

  if (formData.formType === 'EMBED_FORM') {
    FREQUENCY_ORDER.forEach((freq) => {
      if (!checkedValues.includes(freq)) return;

      const index = checkedValues.indexOf(freq);
      const amounts = formData.frequencyAmounts?.[freq];
      if (!amounts) return;

      [
        { key: 'amount1', value: amounts.amount1, label: 'Amount 1' },
        { key: 'amount2', value: amounts.amount2, label: 'Amount 2' },
        { key: 'amount3', value: amounts.amount3, label: 'Amount 3' },
        { key: 'amount4', value: amounts.amount4, label: 'Amount 4' },
      ].forEach((field) => {
        const normalizedFreq = freq.toLowerCase().replace('-', '');
        addAmountValidation(
          invalidFields,
          errorMessages,
          `form_configuration-amounts-${normalizedFreq}-${index}-${field.key}`,
          `${freq} ${field.label}`,
          field.value,
        );
      });

      const normalizedFreq = freq.toLowerCase().replace('-', '');
      addOptionalAmountValidation(
        invalidFields,
        errorMessages,
        `form_configuration-amounts-${normalizedFreq}-${index}-defaultAmount`,
        `${freq} Default Suggested Amount`,
        amounts.defaultAmount,
      );
    });
  } else if (formData.formType === 'CROWDFUNDING_FORM') {
    const optionsMap = {
      'One-Time': formData.oneTimeCrowdfundingOptions,
      Daily: formData.dailyCrowdfundingOptions,
      Weekly: formData.weeklyCrowdfundingOptions,
      Monthly: formData.monthlyCrowdfundingOptions,
      Annually: formData.annuallyCrowdfundingOptions,
    };

    for (const freq of checkedValues.filter((freq) => freq !== '')) {
      const freqOptions = (optionsMap[freq as Frequency] || []) as any[];
      if (freqOptions.length === 0) {
        invalidFields.add(`form_configuration-amounts-crowdfunding-${freq}-missing`);
        errorMessages.push(`${freq}: At least one Giving Level is required`);
        continue;
      }

      freqOptions.forEach((level, index) => {
        if (!level.title || !level.title.trim()) {
          invalidFields.add(`form_configuration-amounts-crowdfunding-${freq}-${index}-title`);
          errorMessages.push(`${freq} - Giving Level ${index + 1}: Title is required`);
        }
        addAmountValidation(
          invalidFields,
          errorMessages,
          `form_configuration-amounts-crowdfunding-${freq}-${index}-amount`,
          `${freq} - Giving Level ${index + 1}: Amount`,
          level.amount,
        );
        if (!level.description || !level.description.trim()) {
          invalidFields.add(`form_configuration-amounts-crowdfunding-${freq}-${index}-description`);
          errorMessages.push(`${freq} - Giving Level ${index + 1}: Description is required`);
        }
      });
    }

    const defaultAmount =
      formData.defaultDonationType && formData.frequencyAmounts?.[formData.defaultDonationType]
        ? formData.frequencyAmounts[formData.defaultDonationType].defaultAmount
        : '';
    addOptionalAmountValidation(
      invalidFields,
      errorMessages,
      'form_configuration-amounts-crowdfunding-defaultAmount',
      'Default Suggested Amount',
      defaultAmount,
    );
  } else if (formData.formType === 'PRODUCT') {
    const enabledFrequencies = checkedValues.filter((freq) => freq !== '');
    const productGroupOptionsMap = reconcileProductGroupsForEnabledFrequencies(formData);
    validatedFormData = productGroupMapToFormData(formData, productGroupOptionsMap);

    for (const freq of enabledFrequencies) {
      const freqGroups = (productGroupOptionsMap[freq] || []) as ProductGroup[];
      if (freqGroups.length === 0) {
        invalidFields.add(`form_configuration-amounts-sponsorship-${freq}-missing`);
        errorMessages.push(`${freq}: At least one Sponsorship Group is required`);
        continue;
      }

      freqGroups.forEach((group, groupIndex) => {
        if (!group.productGroupName || !group.productGroupName.trim()) {
          invalidFields.add(`form_configuration-amounts-sponsorship-${freq}-${groupIndex}-name`);
          errorMessages.push(
            `${freq} - Sponsorship Group ${groupIndex + 1}: Group Title is required`,
          );
        }

        if (!Array.isArray(group.products) || group.products.length === 0) {
          invalidFields.add(
            `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-products`,
          );
          errorMessages.push(
            `${freq} - Sponsorship Group ${groupIndex + 1}: At least one item is required`,
          );
          return;
        }

        group.products.forEach((product, productIndex) => {
          if (!product.productName || !product.productName.trim()) {
            invalidFields.add(
              `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-name`,
            );
            errorMessages.push(
              `${freq} - Sponsorship Group ${groupIndex + 1}, Item ${
                productIndex + 1
              }: Title is required`,
            );
          }
          addAmountValidation(
            invalidFields,
            errorMessages,
            `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-price`,
            `${freq} - Sponsorship Group ${groupIndex + 1}, Item ${productIndex + 1}: Amount`,
            product.productPrice,
          );
        });
      });
    }

    const firstFreq = enabledFrequencies[0];
    if (firstFreq && enabledFrequencies.length > 1) {
      const firstFreqGroupNames = ((productGroupOptionsMap[firstFreq] || []) as ProductGroup[])
        .map((group) => group.productGroupName)
        .sort();

      for (let index = 1; index < enabledFrequencies.length; index += 1) {
        const freq = enabledFrequencies[index];
        const freqGroupNames = ((productGroupOptionsMap[freq] || []) as ProductGroup[])
          .map((group) => group.productGroupName)
          .sort();

        if (JSON.stringify(firstFreqGroupNames) !== JSON.stringify(freqGroupNames)) {
          invalidFields.add('form_configuration-amounts-sponsorship-nameMismatch');
          errorMessages.push(
            `Sponsorship Group names must match across all frequencies. ` +
              `${firstFreq} has: [${firstFreqGroupNames.join(', ')}], ` +
              `${freq} has: [${freqGroupNames.join(', ')}]`,
          );
          break;
        }
      }
    }
  }

  if (Array.isArray(formData.customInputs) && formData.customInputs.length > 0) {
    formData.customInputs.forEach((input: any, index: number) => {
      if (!input.title || input.title.trim() === '') {
        invalidFields.add(`custom_fields-customInput-title-${index}`);
        errorMessages.push('Custom Input Label is required');
      }
      if (!input.type || input.type === 'Select an Input Type') {
        invalidFields.add(`custom_fields-customInput-type-${index}`);
        errorMessages.push('Custom Input Type is required');
      }
      if (
        (input.type === 'SINGLE_SELECTION_DROPDOWN' || input.type === 'MULTI_SELECTION_DROPDOWN') &&
        (!Array.isArray(input.options) || input.options.length < 2)
      ) {
        invalidFields.add(`custom_fields-customInput-options-${index}`);
        errorMessages.push('2 or more Custom Input Options are required for Dropdowns');
      }
    });
  }

  if (Array.isArray(formData.faqs) && formData.faqs.length > 0) {
    formData.faqs.forEach((faq: any, index: number) => {
      if (!faq.question || faq.question.trim() === '') {
        invalidFields.add(`faqs-faq-question-${index}`);
        errorMessages.push('FAQ question is required');
      }
      if (!faq.answer || faq.answer.trim() === '') {
        invalidFields.add(`faqs-faq-answer-${index}`);
        errorMessages.push('FAQ answer is required');
      }
    });
  }

  if (formData.emailConfig?.enableCustomEmail === true) {
    if (!formData.emailConfig.customMessage || formData.emailConfig.customMessage.trim() === '') {
      invalidFields.add('emails-email-customMessage');
      errorMessages.push('Custom message is required');
    }
    if (!formData.emailConfig.subject || formData.emailConfig.subject.trim() === '') {
      invalidFields.add('emails-email-subject');
      errorMessages.push('Email subject is required');
    }
    if (!formData.emailConfig.replyToAddress || formData.emailConfig.replyToAddress.trim() === '') {
      invalidFields.add('emails-email-replyToAddress');
      errorMessages.push('Reply-to address is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailConfig.replyToAddress)) {
      invalidFields.add('emails-email-replyToAddress');
      errorMessages.push('Reply-to address must be a valid email');
    }
  }

  if (
    formData.enableDedication &&
    (!formData.dedicationTypes || formData.dedicationTypes.length === 0)
  ) {
    invalidFields.add('form_configuration-checkout-dedicationTypes');
    errorMessages.push('At least one Dedication Type must be selected when Dedication is enabled');
  }

  return {
    errorMessages,
    formData: validatedFormData,
    invalidFields,
    warningMessages,
  };
}
